/**
 * FolderCard Component
 * Folder display card for grid view with hover effects and actions
 */

import { forwardRef } from 'react';
import { Card, Stack, Text, Group, ActionIcon } from '@/components/ui';
import {
  IconFolder,
  IconFolderOpen,
  IconEdit,
  IconTrash,
  IconFiles,
} from '@tabler/icons-react';
import { formatFileSize } from '../../utils/formatting';
import type { GradientVariant, AccessRule } from '@/components/ccms-components/types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface FolderInfo {
  id: string;
  name: string;
  path: string;
  color?: string;
  fileCount?: number;
  totalSize?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface FolderCardProps {
  /** Folder information */
  folder: FolderInfo;
  /** Callback when folder is clicked */
  onClick?: (folderId: string) => void;
  /** Callback to rename folder */
  onRename?: (folderId: string) => void;
  /** Callback to delete folder */
  onDelete?: (folderId: string) => void;
  /** Card variant */
  variant?: 'default' | 'elevated' | 'outlined';
  /** Hover effect */
  hoverEffect?: 'glow' | 'lift' | 'scale' | 'border';
  /** Gradient variant for accents */
  gradientVariant?: GradientVariant;
  /** Show action buttons */
  showActions?: boolean;
  /** Access control rule */
  accessRule?: AccessRule;
  /** Additional className */
  className?: string;
}

/**
 * Folder card component for grid view display
 *
 * @example
 * ```tsx
 * <FolderCard
 *   folder={{
 *     id: 'folder-123',
 *     name: 'Documents',
 *     path: '/documents',
 *     fileCount: 42,
 *     totalSize: 10485760,
 *     color: '#3B82F6'
 *   }}
 *   onClick={(id) => navigateToFolder(id)}
 *   onRename={(id) => handleRename(id)}
 *   onDelete={(id) => handleDelete(id)}
 *   hoverEffect="lift"
 * />
 * ```
 */
export const FolderCard = forwardRef<HTMLDivElement, FolderCardProps>(
  (props, ref) => {
    const {
      folder,
      onClick,
      onRename,
      onDelete,
      variant = 'elevated',
      hoverEffect = 'lift',
      showActions = true,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleClick = () => {
      onClick?.(folder.id);
    };

    const handleRename = (e: React.MouseEvent) => {
      e.stopPropagation();
      onRename?.(folder.id);
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(folder.id);
    };

    const folderColor = folder.color || 'var(--mantine-color-blue-5)';

    return (
      <Card
        ref={ref}
        variant={variant}
        hoverable
        hoverEffect={hoverEffect}
        onClick={onClick ? handleClick : undefined}
        style={{
          cursor: onClick ? 'pointer' : 'default',
          borderLeft: `4px solid ${folderColor}`,
        }}
        className={className}
      >
        <Stack gap="md">
          {/* Folder Icon and Name */}
          <Group justify="space-between" align="flex-start">
            <Group gap="sm">
              <div
                style={{
                  backgroundColor: folderColor,
                  borderRadius: 'var(--mantine-radius-md)',
                  padding: 'var(--mantine-spacing-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {onClick ? (
                  <IconFolderOpen size={28} color="white" />
                ) : (
                  <IconFolder size={28} color="white" />
                )}
              </div>

              <Stack gap={4}>
                <Text size="md" fw={600} lineClamp={1} style={accessibilityStyles}>
                  {folder.name}
                </Text>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {folder.path}
                </Text>
              </Stack>
            </Group>

            {showActions && (onRename || onDelete) && (
              <Group gap={4}>
                {onRename && (
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={handleRename}
                    aria-label="Rename folder"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                )}
                {onDelete && (
                  <ActionIcon
                    variant="subtle"
                    gradientVariant="danger"
                    size="sm"
                    onClick={handleDelete}
                    aria-label="Delete folder"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                )}
              </Group>
            )}
          </Group>

          {/* Folder Stats */}
          {(folder.fileCount !== undefined || folder.totalSize !== undefined) && (
            <Group gap="md">
              {folder.fileCount !== undefined && (
                <Group gap={4}>
                  <IconFiles size={14} style={{ opacity: 0.6 }} />
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {folder.fileCount} {folder.fileCount === 1 ? 'file' : 'files'}
                  </Text>
                </Group>
              )}
              {folder.totalSize !== undefined && (
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {formatFileSize(folder.totalSize)}
                </Text>
              )}
            </Group>
          )}
        </Stack>
      </Card>
    );
  }
);

FolderCard.displayName = 'FolderCard';

export default FolderCard;
